import type { Rng } from "../rng";
import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	type PlayerIndex,
	reject,
} from "../types";

/**
 * Race your token to the far side while dropping fences in the other player's
 * way. The rule that makes it a puzzle rather than a spite contest: a fence may
 * never shut somebody out completely. There is always a way round, so the game
 * is about making the other route longer, not about walling a child in.
 */

export const BLOCKER_SIZE = 7;
export const BLOCKER_FENCES = 6;
/** Moves before the race is called on whoever is nearer home. */
export const BLOCKER_MOVE_LIMIT = 80;

/** Row each player is trying to reach. */
const GOAL_ROW: [number, number] = [BLOCKER_SIZE - 1, 0];

export interface RouteBlockerState extends BaseState {
	kind: "routeblocker";
	/** Square index (row * SIZE + column) each token stands on. */
	tokens: [number, number];
	/** Blocked edges, each `"a-b"` with the smaller square first. */
	fences: string[];
	fencesLeft: [number, number];
	movesMade: number;
}

export type RouteBlockerMove = { step: number } | { fence: [number, number] };

const rowOf = (square: number) => Math.floor(square / BLOCKER_SIZE);
const columnOf = (square: number) => square % BLOCKER_SIZE;

/** Canonical name for the wall between two squares, whichever way round. */
export function edgeKey(a: number, b: number): string {
	return a < b ? `${a}-${b}` : `${b}-${a}`;
}

function areNeighbours(a: number, b: number): boolean {
	const rowGap = Math.abs(rowOf(a) - rowOf(b));
	const columnGap = Math.abs(columnOf(a) - columnOf(b));
	return rowGap + columnGap === 1;
}

function neighbours(square: number): number[] {
	const found: number[] = [];
	const row = rowOf(square);
	const column = columnOf(square);
	if (row > 0) found.push(square - BLOCKER_SIZE);
	if (row < BLOCKER_SIZE - 1) found.push(square + BLOCKER_SIZE);
	if (column > 0) found.push(square - 1);
	if (column < BLOCKER_SIZE - 1) found.push(square + 1);
	return found;
}

/** Steps from `from` to the nearest square on `goalRow`, or null if walled in. */
export function stepsHome(
	from: number,
	goalRow: number,
	fences: ReadonlySet<string>,
): number | null {
	if (rowOf(from) === goalRow) return 0;
	const seen = new Set([from]);
	let frontier = [from];
	let depth = 0;

	while (frontier.length > 0) {
		depth += 1;
		const next: number[] = [];
		for (const square of frontier) {
			for (const neighbour of neighbours(square)) {
				if (seen.has(neighbour)) continue;
				if (fences.has(edgeKey(square, neighbour))) continue;
				if (rowOf(neighbour) === goalRow) return depth;
				seen.add(neighbour);
				next.push(neighbour);
			}
		}
		frontier = next;
	}
	return null;
}

/** True when both players can still reach their own side. */
function everyoneCanGetHome(state: RouteBlockerState, fences: ReadonlySet<string>): boolean {
	return (
		stepsHome(state.tokens[0], GOAL_ROW[0], fences) !== null &&
		stepsHome(state.tokens[1], GOAL_ROW[1], fences) !== null
	);
}

function startSquare(player: 0 | 1): number {
	const middle = Math.floor(BLOCKER_SIZE / 2);
	return player === 0 ? middle : (BLOCKER_SIZE - 1) * BLOCKER_SIZE + middle;
}

export const routeBlocker: GameDefinition<RouteBlockerState, RouteBlockerMove> = {
	id: "routeblocker",
	category: "puzzle",
	title: "Route Blocker",
	emoji: "🚧",
	blurb: "Race to the other side while dropping fences in your friend's way.",
	howTo: [
		"Move your token one square, or drop a fence between two squares.",
		"A fence can never shut someone in — there is always a way round.",
		"First token to reach the far side of the board wins.",
	],

	create(rng) {
		return {
			kind: "routeblocker",
			tokens: [startSquare(0), startSquare(1)],
			fences: [],
			fencesLeft: [BLOCKER_FENCES, BLOCKER_FENCES],
			movesMade: 0,
			// The opener has the tempo, so it is drawn by lot rather than handed
			// to whoever happens to sit in seat 0.
			turn: rng.int(2) as PlayerIndex,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const value = raw as { step?: unknown; fence?: unknown };

		if (typeof value.step === "number" && Number.isInteger(value.step)) {
			const square = value.step;
			return square >= 0 && square < BLOCKER_SIZE * BLOCKER_SIZE ? { step: square } : null;
		}

		if (Array.isArray(value.fence) && value.fence.length === 2) {
			const [a, b] = value.fence;
			if (typeof a !== "number" || typeof b !== "number") return null;
			if (!Number.isInteger(a) || !Number.isInteger(b)) return null;
			const limit = BLOCKER_SIZE * BLOCKER_SIZE;
			if (a < 0 || b < 0 || a >= limit || b >= limit) return null;
			return areNeighbours(a, b) ? { fence: [a, b] } : null;
		}
		return null;
	},

	apply(state, player, move, _now): MoveOutcome<RouteBlockerState> {
		if (state.result) return reject("This game is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");

		const fences = new Set(state.fences);
		const movesMade = state.movesMade + 1;
		let next: RouteBlockerState;

		if ("step" in move) {
			const from = state.tokens[player];
			if (!areNeighbours(from, move.step)) return reject("That square isn't next to you.");
			if (fences.has(edgeKey(from, move.step))) return reject("There's a fence in the way.");
			if (state.tokens[otherPlayer(player)] === move.step) {
				return reject("Your friend is standing there.");
			}

			const tokens: [number, number] = [...state.tokens];
			tokens[player] = move.step;
			next = { ...state, tokens, movesMade };

			if (rowOf(move.step) === GOAL_ROW[player]) {
				const scores: [number, number] = [0, 0];
				scores[player] = 1;
				return { ok: true, state: { ...next, scores, turn: null, result: { winner: player } } };
			}
		} else {
			if (state.fencesLeft[player] <= 0) return reject("You've used all your fences.");
			const key = edgeKey(move.fence[0], move.fence[1]);
			if (fences.has(key)) return reject("There's already a fence there.");

			fences.add(key);
			if (!everyoneCanGetHome(state, fences)) {
				return reject("That would shut someone in completely.");
			}

			const fencesLeft: [number, number] = [...state.fencesLeft];
			fencesLeft[player] -= 1;
			next = { ...state, fences: [...fences], fencesLeft, movesMade };
		}

		// Nobody is getting home. Whoever is nearer takes it.
		if (movesMade >= BLOCKER_MOVE_LIMIT) {
			const live = new Set(next.fences);
			const mine = stepsHome(next.tokens[0], GOAL_ROW[0], live) ?? Number.POSITIVE_INFINITY;
			const theirs = stepsHome(next.tokens[1], GOAL_ROW[1], live) ?? Number.POSITIVE_INFINITY;
			const winner = mine === theirs ? null : mine < theirs ? 0 : 1;
			return { ok: true, state: { ...next, turn: null, result: { winner } } };
		}

		// The other player's token can seal the last way out of a corner — the
		// fence rule cannot see that, because it only reasons about fences. If
		// they have nowhere to go, the race is decided on who is nearer home
		// rather than left standing there for ever.
		const rival = otherPlayer(player);
		if (routeBlocker.legalMoves({ ...next, turn: rival }, rival).length === 0) {
			const live = new Set(next.fences);
			const mine =
				stepsHome(next.tokens[player], GOAL_ROW[player], live) ?? Number.POSITIVE_INFINITY;
			const theirs =
				stepsHome(next.tokens[rival], GOAL_ROW[rival], live) ?? Number.POSITIVE_INFINITY;
			const winner = mine === theirs ? null : mine < theirs ? player : rival;
			return { ok: true, state: { ...next, turn: null, result: { winner } } };
		}

		return { ok: true, state: { ...next, turn: rival } };
	},

	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		const fences = new Set(state.fences);
		const moves: RouteBlockerMove[] = [];

		const from = state.tokens[player];
		for (const square of neighbours(from)) {
			if (fences.has(edgeKey(from, square))) continue;
			if (state.tokens[otherPlayer(player)] === square) continue;
			moves.push({ step: square });
		}

		if (state.fencesLeft[player] > 0) {
			for (let square = 0; square < BLOCKER_SIZE * BLOCKER_SIZE; square++) {
				for (const neighbour of neighbours(square)) {
					if (neighbour < square) continue;
					const key = edgeKey(square, neighbour);
					if (fences.has(key)) continue;
					fences.add(key);
					if (everyoneCanGetHome(state, fences)) moves.push({ fence: [square, neighbour] });
					fences.delete(key);
				}
			}
		}
		return moves;
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},
};

/** How far each player still has to go, for the board and for the bot. */
export function distancesHome(state: RouteBlockerState): [number | null, number | null] {
	const fences = new Set(state.fences);
	return [
		stepsHome(state.tokens[0], GOAL_ROW[0], fences),
		stepsHome(state.tokens[1], GOAL_ROW[1], fences),
	];
}

/**
 * Walk if walking helps; fence if a fence costs the other player more than it
 * costs you. Deliberately not a search — a perfect Route Blocker opponent is
 * miserable to play against, and this one can be out-thought.
 *
 * Equally-short steps are split by the caller's dice when it offers any:
 * always walking the first-listed of two equal paths made two hard bots
 * replay one identical game, with the same seat winning every time.
 */
export function bestBlockerMove(
	state: RouteBlockerState,
	player: 0 | 1,
	options: { useFences: boolean; rng?: Rng },
): RouteBlockerMove | null {
	const moves = routeBlocker.legalMoves(state, player);
	if (moves.length === 0) return null;

	const fences = new Set(state.fences);
	const rival = otherPlayer(player);
	const myDistance = stepsHome(state.tokens[player], GOAL_ROW[player], fences);

	let bestSteps: RouteBlockerMove[] = [];
	let bestStepDistance = Number.POSITIVE_INFINITY;
	for (const move of moves) {
		if (!("step" in move)) continue;
		const distance = stepsHome(move.step, GOAL_ROW[player], fences) ?? Number.POSITIVE_INFINITY;
		if (distance < bestStepDistance) {
			bestStepDistance = distance;
			bestSteps = [move];
		} else if (distance === bestStepDistance) {
			bestSteps.push(move);
		}
	}
	const bestStep =
		bestSteps.length === 0 ? null : bestSteps[options.rng ? options.rng.int(bestSteps.length) : 0];

	if (!options.useFences || state.fencesLeft[player] <= 0) return bestStep ?? moves[0];

	// A fence is worth it only when it sets them back further than it sets us.
	const theirDistance = stepsHome(state.tokens[rival], GOAL_ROW[rival], fences) ?? 0;
	let bestFence: RouteBlockerMove | null = null;
	let bestGain = 0;

	for (const move of moves) {
		if (!("fence" in move)) continue;
		const key = edgeKey(move.fence[0], move.fence[1]);
		fences.add(key);
		const theirs = stepsHome(state.tokens[rival], GOAL_ROW[rival], fences) ?? theirDistance;
		const mine = stepsHome(state.tokens[player], GOAL_ROW[player], fences) ?? myDistance ?? 0;
		fences.delete(key);

		const gain = theirs - theirDistance - (mine - (myDistance ?? 0));
		if (gain > bestGain) {
			bestGain = gain;
			bestFence = move;
		}
	}

	// Only worth a fence if we are not already winning the footrace.
	const behind = (myDistance ?? 0) >= theirDistance;
	return bestFence && behind ? bestFence : (bestStep ?? moves[0]);
}
